import { cn } from '../../../lib/styles';
import { Icon } from '../icon/icon.spark';
import type { IconSize } from '../icon/types';
import { Spinner } from '../spinner/spinner.spark';
import { collapsedLabelClass } from './button.shared';
import type { ButtonProps, ButtonSize } from './types';

const BUTTON_ICON_SIZE: Record<ButtonSize, IconSize> = {
  xs: 'xs',
  sm: 'sm',
};

type SizeConfig = {
  base: string;
  iconOnly: string;
  iconOnlyFromSm: string;
  paddedFromSm: string;
};

const SIZE_CONFIG: Record<ButtonSize, SizeConfig> = {
  xs: {
    base: 'h-6 px-2 gap-x-0.5 text-xs rounded-md',
    iconOnly: 'w-6 px-0',
    iconOnlyFromSm: 'sm:w-6 sm:px-0',
    paddedFromSm: 'sm:w-fit sm:px-1.5',
  },
  sm: {
    base: 'h-spark-component px-3 gap-x-1 text-sm rounded-lg',
    iconOnly: 'w-spark-component px-0',
    iconOnlyFromSm: 'sm:w-spark-component sm:px-0',
    paddedFromSm: 'sm:w-fit sm:px-3',
  },
};

export function Button(_props: ButtonProps) {
  const { variant = 'primary', size = 'sm', type = 'button', ...props } = _props;

  const isDisabled = props.disabled || props.loading;

  const hasGraphic = props.icon || props.loading; // Showing icon or spinner
  const isIconOnly = hasGraphic && !props.children;

  const collapseLabel = hasGraphic && props.children ? props.collapseLabel : undefined;

  const sizeConfig = SIZE_CONFIG[size];

  const graphic = props.loading ? (
    <Spinner />
  ) : (
    props.icon && (
      <span aria-hidden>
        <Icon name={props.icon} size={BUTTON_ICON_SIZE[size]} />
      </span>
    )
  );

  return (
    <button
      formNoValidate
      type={type}
      onClick={props.onClick}
      disabled={isDisabled}
      aria-busy={props.loading}
      aria-label={props.ariaLabel}
      aria-expanded={props.ariaExpanded}
      aria-controls={props.ariaControls}
      aria-current={props.ariaCurrent}
      className={cn(
        'flex w-fit shrink-0 items-center justify-center whitespace-nowrap border border-spark-border-contrast text-spark-text-primary transition focus:outline-hidden',
        sizeConfig.base,
        (isIconOnly || collapseLabel === 'mobile') && sizeConfig.iconOnly,
        collapseLabel === 'mobile' && sizeConfig.paddedFromSm,
        collapseLabel === 'desktop' && sizeConfig.iconOnlyFromSm,
        props.className,
        isDisabled
          ? 'cursor-not-allowed border-spark-border-default bg-spark-surface-muted text-spark-text-secondary'
          : 'cursor-pointer shadow-xs focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-spark-surface',
        !isDisabled && {
          'bg-spark-primary text-spark-text-inverse hover:brightness-125 focus-visible:ring-spark-primary-accent':
            variant === 'primary',
          'border-spark-border-default bg-spark-surface hover:bg-spark-surface-hover focus-visible:ring-spark-primary-accent':
            variant === 'secondary',
          'bg-spark-danger-solid text-spark-text-inverse hover:bg-spark-danger-solid-hover focus-visible:ring-spark-danger-solid':
            variant === 'danger',
          'border-spark-border-default bg-spark-surface hover:border-spark-danger-solid hover:bg-spark-danger-subtle hover:text-spark-danger-text focus-visible:ring-spark-danger-solid':
            variant === 'dangerOutline',
          'border-none shadow-none hover:border-spark-text-danger hover:text-spark-text-danger focus-visible:ring-spark-text-danger':
            variant === 'dangerBare',
          'border-none shadow-none hover:bg-spark-overlay-hover focus-visible:ring-spark-primary-accent':
            variant === 'bare',
        },
      )}
    >
      {props.iconPosition !== 'end' && graphic}
      {props.children && (
        <span className={collapsedLabelClass(collapseLabel)}>{props.children}</span>
      )}
      {props.iconPosition === 'end' && graphic}
    </button>
  );
}
